import { API_BASE_URL } from './client';
import { consumeChatNdjsonStream } from './streamNdjson';

// RAG Chat API
export const sendRAGChatMessage = async (keycloak, { collectionId, messages, model = 'mistral:7b', assistantId = null }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/rag/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify({
        collection_id: collectionId,
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        model,
        assistant_id: assistantId
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      // Handle both string and object error details
      let errorDetail = error.detail || 'Failed to send message';
      if (typeof errorDetail === 'object' && errorDetail.message) {
        // Create error with both friendly message and full error
        const err = new Error(errorDetail.message);
        err.fullError = errorDetail.full_error || errorDetail.message;
        throw err;
      }
      throw new Error(errorDetail);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error sending RAG chat message:', error);
    throw error;
  }
};

/** RAG chat with streaming NDJSON (same shape as non-streaming for errors). */
export const sendRAGChatMessageStream = async (
  keycloak,
  { collectionId, messages, model = 'mistral:7b', assistantId = null },
  { onDelta, onDone, onPipelineTrace }
) => {
  const response = await fetch(`${API_BASE_URL}/chat/rag/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${keycloak.token}`,
    },
    body: JSON.stringify({
      collection_id: collectionId,
      messages: messages.map((msg) => ({ role: msg.role, content: msg.content })),
      model,
      assistant_id: assistantId,
      stream: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let errorDetail = error.detail || 'Failed to send message';
    if (typeof errorDetail === 'object' && errorDetail.message) {
      const err = new Error(errorDetail.message);
      err.fullError = errorDetail.full_error || errorDetail.message;
      throw err;
    }
    throw new Error(errorDetail);
  }

  await consumeChatNdjsonStream(response, { onDelta, onDone, onPipelineTrace });
};

export const saveChatMessage = async (keycloak, {
  assistantId, sessionId, role, content, isError = false,
  pipeline_trace: pipelineTrace = undefined,
  message_metadata: messageMetadata = undefined,
}) => {
  const body = { assistant_id: assistantId, session_id: sessionId, role, content, is_error: isError };
  if (pipelineTrace != null) body.pipeline_trace = pipelineTrace;
  if (messageMetadata != null) body.message_metadata = messageMetadata;
  const response = await fetch(`${API_BASE_URL}/chat/messages/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${keycloak.token}` },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('Failed to save message');
  return response.json();
};

export const getChatMessages = async (keycloak, assistantId, sessionId) => {
  const params = sessionId ? `?session_id=${sessionId}` : '';
  const response = await fetch(`${API_BASE_URL}/chat/messages/${assistantId}/${params}`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to load messages');
  return response.json();
};

export const getChatSessions = async (keycloak, assistantId) => {
  const response = await fetch(`${API_BASE_URL}/chat/sessions/${assistantId}/`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to load sessions');
  return response.json();
};

export const submitMessageFeedback = async (keycloak, { messageId, assistantId, feedback }) => {
  const response = await fetch(`${API_BASE_URL}/chat/feedback/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${keycloak.token}` },
    body: JSON.stringify({ message_id: messageId, assistant_id: assistantId, feedback }),
  });
  if (!response.ok) throw new Error('Failed to submit feedback');
  return response.json();
};

export const removeMessageFeedback = async (keycloak, messageId) => {
  const response = await fetch(`${API_BASE_URL}/chat/feedback/?message_id=${messageId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to remove feedback');
  return response.json();
};

export const clearChatHistory = async (keycloak, assistantId) => {
  const response = await fetch(`${API_BASE_URL}/chat/messages/${assistantId}/`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to clear chat history');
  return response.json();
};
